import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { showToast } from '../utils/toast';
import { sanitizeInput } from '../utils/sanitize';
import { validatePassword, getPasswordStrengthColor } from '../utils/passwordValidation';
import { API_BASE_URL } from '../config/api';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import { TableSkeleton } from './LoadingSkeleton';
import './AdminConsole.css';

function AdminConsole() {
  const { user } = useAuth();
  const { locale, foodDisplayName } = useLanguage();
  const [users, setUsers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateCoach, setShowCreateCoach] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'customer', meal_count: 6, workout_count: 6 });
  const [newCoach, setNewCoach] = useState({ username: '', email: '', password: '', expiration_date: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ username: '', role: '', meal_count: 6, workout_count: 6 });
  const [foods, setFoods] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [newFood, setNewFood] = useState('');
  const [newFoodEn, setNewFoodEn] = useState('');
  const [newSupplement, setNewSupplement] = useState('');
  const [newExercise, setNewExercise] = useState('');
  const [selectedFoodLetter, setSelectedFoodLetter] = useState('all');
  const [selectedSuppLetter, setSelectedSuppLetter] = useState('all');
  const [selectedExerciseLetter, setSelectedExerciseLetter] = useState('all');
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [editingExerciseDraft, setEditingExerciseDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState({ strength: 'weak', errors: [] });
  const [resettingPasswordFor, setResettingPasswordFor] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordStrength, setResetPasswordStrength] = useState({ strength: 'weak', errors: [] });
  const [editingNutritionFor, setEditingNutritionFor] = useState(null);
  const [nutritionForm, setNutritionForm] = useState({
    protein_per_100g: 0,
    carbs_per_100g: 0,
    fats_per_100g: 0,
    calories_per_100g: 0,
    name_en: ''
  });
  const [changingExpirationFor, setChangingExpirationFor] = useState(null);
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'super_admin' || user.role === 'coach')) {
      const promises = [
        fetchUsers(),
        fetchFoods(),
        fetchSupplements(),
        fetchExercises()
      ];
      
      if (user.role === 'super_admin') {
        promises.push(fetchCoaches());
      }
      
      Promise.all(promises).finally(() => setLoading(false));
    }
  }, [user]);

  useEffect(() => {
    if (!editingExerciseId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setEditingExerciseId(null);
        setEditingExerciseDraft('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingExerciseId]);

  useEffect(() => {
    setSelectedFoodLetter('all');
  }, [locale]);

  useEffect(() => {
    setEditingExerciseId(null);
    setEditingExerciseDraft('');
  }, [selectedExerciseLetter]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      // Ensure we always set an array, even if the response is unexpected
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Set empty array on error to prevent crashes
      setUsers([]);
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/coaches`);
      // Ensure we always set an array, even if the response is unexpected
      setCoaches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching coaches:', error);
      // Set empty array on error to prevent crashes
      setCoaches([]);
    }
  };

  const fetchFoods = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/foods`);
      // Ensure we always set an array, even if the response is unexpected
      const foodsData = Array.isArray(response.data) ? response.data : [];
      setFoods(foodsData.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching foods:', error);
      // Set empty array on error to prevent crashes
      setFoods([]);
    }
  };

  const fetchSupplements = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/supplements`);
      // Ensure we always set an array, even if the response is unexpected
      const supplementsData = Array.isArray(response.data) ? response.data : [];
      setSupplements(supplementsData.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching supplements:', error);
      // Set empty array on error to prevent crashes
      setSupplements([]);
    }
  };

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/exercises`);
      // Ensure we always set an array, even if the response is unexpected
      const exercisesData = Array.isArray(response.data) ? response.data : [];
      setExercises(exercisesData.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching exercises:', error);
      // Set empty array on error to prevent crashes
      setExercises([]);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    // Verify user is admin, super_admin, or coach
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin' && user.role !== 'coach')) {
      showToast.error('You must be an admin or coach to create users');
      return;
    }
    
    // Validate password strength
    if (newUser.password) {
      const validation = validatePassword(newUser.password);
      if (!validation.isValid) {
        showToast.error('Password does not meet requirements: ' + validation.errors.join(', '));
        return;
      }
    }
    
    const userData = {
      ...newUser,
      username: sanitizeInput(newUser.username),
      password: newUser.password,
      meal_count: newUser.meal_count || 6,
      workout_count: newUser.workout_count || 6
    };
    
    try {
      await axios.post(`${API_BASE_URL}/api/users`, userData);
      showToast.success('User created successfully!');
      setShowCreateUser(false);
      setNewUser({ username: '', password: '', role: 'customer', meal_count: 6, workout_count: 6 });
      setPasswordStrength({ strength: 'weak', errors: [] });
      fetchUsers();
    } catch (error) {
      console.error('Create user error:', error);
      if (error.response?.status === 403) {
        showToast.error('Access denied. Please log out and log in again as an admin.');
      } else if (error.response?.status === 401) {
        showToast.error('Session expired. Please log in again.');
      } else {
        showToast.error('Error creating user: ' + (error.response?.data?.error || 'Unknown error'));
      }
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit.id);
    setEditForm({
      username: userToEdit.username,
      role: userToEdit.role,
      meal_count: userToEdit.meal_count || 6,
      workout_count: userToEdit.workout_count || 6
    });
  };

  const handleUpdateUser = async () => {
    try {
      const response = await axios.put(`${API_BASE_URL}/api/users/${editingUser}`, {
        ...editForm,
        username: sanitizeInput(editForm.username)
      });
      console.log('Update response:', response.data);
      showToast.success('User updated successfully!');
      setEditingUser(null);
      setEditForm({ username: '', role: '', meal_count: 6, workout_count: 6 });
      fetchUsers();
    } catch (error) {
      console.error('Update error:', error);
      showToast.error('Error updating user: ' + (error.response?.data?.error || error.message || 'Unknown error'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/api/users/${userId}`);
      showToast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      showToast.error('Error deleting user: ' + errorMessage);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword.trim()) {
      showToast.warning('Please enter a new password');
      return;
    }

    // Validate password strength
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      showToast.error('Password does not meet requirements: ' + validation.errors.join(', '));
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/users/${userId}/reset-password`, {
        new_password: newPassword
      });
      showToast.success('Password reset successfully!');
      setResettingPasswordFor(null);
      setNewPassword('');
      setResetPasswordStrength({ strength: 'weak', errors: [] });
      fetchUsers();
    } catch (error) {
      showToast.error('Error resetting password: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleChangeExpiration = async (userId) => {
    if (!newExpirationDate) {
      showToast.warning('Please select an expiration date');
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/api/users/${userId}/expiration`, {
        expiration_date: newExpirationDate
      });
      showToast.success('Expiration date updated successfully!');
      setChangingExpirationFor(null);
      setNewExpirationDate('');
      fetchUsers();
    } catch (error) {
      showToast.error('Error updating expiration date: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const today = new Date();
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 3 && diffDays >= 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleAddFood = async (e) => {
    e.preventDefault();
    const sanitizedFood = sanitizeInput(newFood);
    if (!sanitizedFood.trim()) {
      showToast.warning('Please enter a food name');
      return;
    }
    try {
      const payload = { name: sanitizedFood };
      const en = sanitizeInput(newFoodEn).trim();
      if (en) payload.name_en = en;
      await axios.post(`${API_BASE_URL}/api/foods`, payload);
      setNewFood('');
      setNewFoodEn('');
      showToast.success('Food added successfully');
      fetchFoods();
    } catch (error) {
      showToast.error('Error adding food: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleAddSupplement = async (e) => {
    e.preventDefault();
    const sanitizedSupplement = sanitizeInput(newSupplement);
    if (!sanitizedSupplement.trim()) {
      showToast.warning('Please enter a supplement name');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/supplements`, { name: sanitizedSupplement });
      setNewSupplement('');
      showToast.success('Supplement added successfully');
      fetchSupplements();
    } catch (error) {
      showToast.error('Error adding supplement: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleAddExercise = async (e) => {
    e.preventDefault();
    const sanitizedExercise = sanitizeInput(newExercise);
    if (!sanitizedExercise.trim()) {
      showToast.warning('Please enter an exercise name');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/exercises`, { name: sanitizedExercise });
      setNewExercise('');
      showToast.success('Exercise added successfully');
      fetchExercises();
    } catch (error) {
      showToast.error('Error adding exercise: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleSaveExerciseRename = async (id) => {
    const sanitized = sanitizeInput(editingExerciseDraft);
    if (!sanitized.trim()) {
      showToast.warning('Please enter a name');
      return;
    }
    try {
      await axios.put(`${API_BASE_URL}/api/exercises/${id}`, { name: sanitized.trim() });
      showToast.success('Exercise updated');
      setEditingExerciseId(null);
      setEditingExerciseDraft('');
      await fetchExercises();
    } catch (error) {
      showToast.error(error.response?.data?.error || 'Error updating exercise');
    }
  };

  const handleDownloadNutrition = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/foods/nutrition/export`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'food-nutrition.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showToast.success('Nutrition data downloaded successfully!');
    } catch (error) {
      showToast.error('Error downloading nutrition data: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleImportNutritionCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.csv')) {
      showToast.error('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n');
        
        // Skip header row
        const dataLines = lines.slice(1).filter(line => line.trim());
        
        // Parse CSV
        const nutritionData = dataLines.map(line => {
          const columns = line.split(',');
          if (columns.length < 5) return null;
          
          return {
            name: columns[0].trim(),
            protein_per_100g: parseFloat(columns[1]) || 0,
            carbs_per_100g: parseFloat(columns[2]) || 0,
            fats_per_100g: parseFloat(columns[3]) || 0,
            calories_per_100g: parseFloat(columns[4]) || 0
          };
        }).filter(item => item && item.name);

        if (nutritionData.length === 0) {
          showToast.error('No valid nutrition data found in CSV');
          return;
        }

        // Upload to server
        const response = await axios.post(`${API_BASE_URL}/api/import/nutrition`, {
          nutritionData
        });

        showToast.success(
          `Nutrition data imported! Updated: ${response.data.updated}, Not found: ${response.data.notFound}`
        );
        
        if (response.data.notFoundNames && response.data.notFoundNames.length > 0) {
          console.warn('Foods not found:', response.data.notFoundNames);
        }

        // Refresh foods list
        fetchFoods();
        
        // Reset file input
        event.target.value = '';
      } catch (error) {
        showToast.error('Error importing nutrition data: ' + (error.response?.data?.error || 'Unknown error'));
      }
    };

    reader.readAsText(file);
  };

  const handleEditNutrition = (food) => {
    setEditingNutritionFor(food.id);
    setNutritionForm({
      protein_per_100g: food.protein_per_100g || 0,
      carbs_per_100g: food.carbs_per_100g || 0,
      fats_per_100g: food.fats_per_100g || 0,
      calories_per_100g: food.calories_per_100g || 0,
      serving_type: food.serving_type || 'grams',
      grams_per_serving: food.grams_per_serving || (food.serving_type === 'piece' ? 50 : 100),
      name_en: food.name_en || ''
    });
  };

  const handleSaveNutrition = async (foodId) => {
    try {
      await axios.put(`${API_BASE_URL}/api/foods/${foodId}/nutrition`, nutritionForm);
      showToast.success('Nutrition updated successfully!');
      setEditingNutritionFor(null);
      setNutritionForm({
        protein_per_100g: 0,
        carbs_per_100g: 0,
        fats_per_100g: 0,
        calories_per_100g: 0,
        serving_type: 'grams',
        grams_per_serving: 100,
        name_en: ''
      });
      fetchFoods();
    } catch (error) {
      showToast.error('Error updating nutrition: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const foodLetters = useMemo(() => {
    const letters = new Set(
      (Array.isArray(foods) ? foods : []).map((f) =>
        foodDisplayName(f.name, f.name_en).charAt(0).toUpperCase()
      )
    );
    return Array.from(letters).sort((a, b) => a.localeCompare(b, locale === 'en' ? 'en' : 'sr-Latn'));
  }, [foods, foodDisplayName, locale]);

  const getSupplementLetters = () => {
    const letters = new Set(Array.isArray(supplements) ? supplements.map(s => s.name.charAt(0).toUpperCase()) : []);
    return Array.from(letters).sort();
  };

  const getExerciseLetters = () => {
    const letters = new Set(Array.isArray(exercises) ? exercises.map(e => e.name.charAt(0).toUpperCase()) : []);
    return Array.from(letters).sort();
  };

  const filteredFoods = useMemo(() => {
    const list = Array.isArray(foods) ? foods : [];
    if (selectedFoodLetter === 'all') return list;
    return list.filter(
      (f) => foodDisplayName(f.name, f.name_en).charAt(0).toUpperCase() === selectedFoodLetter
    );
  }, [foods, selectedFoodLetter, foodDisplayName]);

  const filteredSupplements = selectedSuppLetter === 'all' 
    ? (Array.isArray(supplements) ? supplements : [])
    : (Array.isArray(supplements) ? supplements.filter(s => s.name.charAt(0).toUpperCase() === selectedSuppLetter) : []);

  const filteredExercises = selectedExerciseLetter === 'all' 
    ? (Array.isArray(exercises) ? exercises : [])
    : (Array.isArray(exercises) ? exercises.filter(e => e.name.charAt(0).toUpperCase() === selectedExerciseLetter) : []);

  return (
    <div className="admin-console">
      <div className="container">
        <h1>Admin Console</h1>

        {/* User Management Section */}
        <div className="card">
          <div className="section-header">
            <h2>User Management</h2>
            <button onClick={() => setShowCreateUser(!showCreateUser)} className="btn-primary">
              {showCreateUser ? 'Cancel' : 'Create New User'}
            </button>
          </div>

          {showCreateUser && (
            <form onSubmit={handleCreateUser} className="create-user-form">
              <input
                type="text"
                placeholder="Username"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
                className="large-input"
              />
              <input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) => {
                  const password = e.target.value;
                  setNewUser({ ...newUser, password });
                  if (password) {
                    const validation = validatePassword(password);
                    setPasswordStrength(validation);
                  } else {
                    setPasswordStrength({ strength: 'weak', errors: [] });
                  }
                }}
                required
                className="large-input"
              />
              {newUser.password && (
                <div style={{ fontSize: '12px', marginTop: '-8px', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span>Strength:</span>
                    <span style={{ 
                      color: getPasswordStrengthColor(passwordStrength.strength),
                      fontWeight: 600,
                      textTransform: 'capitalize'
                    }}>
                      {passwordStrength.strength}
                    </span>
                  </div>
                  {passwordStrength.errors.length > 0 && (
                    <div style={{ fontSize: '11px', color: '#dc3545' }}>
                      {passwordStrength.errors.map((error, i) => (
                        <div key={i}>• {error}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="large-input"
              >
                <option value="customer">Customer</option>
                {user && user.role === 'super_admin' && (
                  <option value="coach">Coach</option>
                )}
              </select>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>Meals per Day</label>
                <input
                  type="number"
                  placeholder="Meals per Day (default: 6)"
                  value={newUser.meal_count}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewUser({ ...newUser, meal_count: value === '' ? '' : parseInt(value) });
                  }}
                  min="1"
                  max="20"
                  className="large-input"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>Workouts per Week</label>
                <input
                  type="number"
                  placeholder="Workouts per Week (default: 6)"
                  value={newUser.workout_count}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewUser({ ...newUser, workout_count: value === '' ? '' : parseInt(value) });
                  }}
                  min="1"
                  max="10"
                  className="large-input"
                />
              </div>
              <button type="submit" className="btn-primary">Create User</button>
            </form>
          )}

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Role</th>
                  {user && user.role === 'super_admin' && <th>Coach</th>}
                  <th>Meals/Day</th>
                  <th>Workouts/Week</th>
                  <th>Expiration Date</th>
                  <th>Created By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(users) && users.map((u) => {
                  const expiringSoon = isExpiringSoon(u.expiration_date);
                  return (
                  <tr key={u.id} className={expiringSoon ? 'expiring-soon' : ''}>
                    <td>{u.id}</td>
                    {editingUser === u.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={editForm.username}
                            onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                            className="edit-input"
                          />
                        </td>
                        <td>
                          <select
                            value={editForm.role}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                            className="edit-input"
                          >
                            <option value="customer">Customer</option>
                            {user && user.role === 'super_admin' && <option value="coach">Coach</option>}
                          </select>
                        </td>
                        {user && user.role === 'super_admin' && (
                          <td>—</td>
                        )}
                        <td>
                          <input
                            type="number"
                            value={editForm.meal_count}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEditForm({ ...editForm, meal_count: value === '' ? '' : parseInt(value) });
                            }}
                            min="1"
                            max="20"
                            className="edit-input"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={editForm.workout_count}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEditForm({ ...editForm, workout_count: value === '' ? '' : parseInt(value) });
                            }}
                            min="1"
                            max="10"
                            className="edit-input"
                          />
                        </td>
                        <td className={isExpiringSoon(u.expiration_date) ? 'expiring-date' : ''}>{formatDate(u.expiration_date)}</td>
                        <td>{u.created_by || '-'}</td>
                        <td>
                          <button onClick={handleUpdateUser} className="btn-primary save-btn">Save</button>
                          <button onClick={() => setEditingUser(null)} className="btn-secondary">Cancel</button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{u.username}</td>
                        <td>
                          <span className={`role-badge ${u.role}`}>{u.role}</span>
                        </td>
                        {user && user.role === 'super_admin' && (
                          <td>
                            {u.role === 'coach' ? '—' : (Array.isArray(coaches) && coaches.find(c => c.id === u.coach_id)?.username || '—')}
                          </td>
                        )}
                        <td>{u.meal_count || 6}</td>
                        <td>{u.workout_count || 6}</td>
                        <td className={expiringSoon ? 'expiring-date' : ''}>{formatDate(u.expiration_date)}</td>
                        <td>{u.created_by || '-'}</td>
                        <td className="action-buttons">
                          <button
                            onClick={() => handleEditUser(u)}
                            className="btn-secondary"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setResettingPasswordFor(u.id);
                              setNewPassword('');
                              setResetPasswordStrength({ strength: 'weak', errors: [] });
                            }}
                            className="btn-secondary"
                            style={{ backgroundColor: '#914c62' }}
                          >
                            Reset Password
                          </button>
                          <button
                            onClick={() => {
                              setChangingExpirationFor(u.id);
                              setNewExpirationDate(u.expiration_date || '');
                            }}
                            className="btn-secondary"
                            style={{ backgroundColor: '#ff9800' }}
                          >
                            Change Expiration
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className="btn-danger"
                          >
                            Delete
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Change Expiration Date Modal */}
          {changingExpirationFor && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '30px',
                borderRadius: '10px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}>
                <h3 style={{ marginTop: 0, color: '#1c4e4f' }}>
                  Change Expiration Date for {Array.isArray(users) && users.find(u => u.id === changingExpirationFor)?.username}
                </h3>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                    New Expiration Date:
                  </label>
                  <input
                    type="date"
                    value={newExpirationDate}
                    onChange={(e) => setNewExpirationDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setChangingExpirationFor(null);
                      setNewExpirationDate('');
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleChangeExpiration(changingExpirationFor)}
                    className="btn-primary"
                    disabled={!newExpirationDate}
                  >
                    Update Expiration
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Password Reset Modal */}
          {resettingPasswordFor && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '30px',
                borderRadius: '10px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}>
                <h3 style={{ marginTop: 0, color: '#1c4e4f' }}>
                  Reset Password for {Array.isArray(users) && users.find(u => u.id === resettingPasswordFor)?.username}
                </h3>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                    New Password:
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => {
                      const password = e.target.value;
                      setNewPassword(password);
                      if (password) {
                        const validation = validatePassword(password);
                        setResetPasswordStrength(validation);
                      } else {
                        setResetPasswordStrength({ strength: 'weak', errors: [] });
                      }
                    }}
                    placeholder="Enter new password"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {newPassword && (
                    <div style={{ fontSize: '12px', marginTop: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span>Strength:</span>
                        <span style={{ 
                          color: getPasswordStrengthColor(resetPasswordStrength.strength),
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {resetPasswordStrength.strength}
                        </span>
                      </div>
                      {resetPasswordStrength.errors.length > 0 && (
                        <div style={{ fontSize: '11px', color: '#dc3545' }}>
                          {resetPasswordStrength.errors.map((error, i) => (
                            <div key={i}>• {error}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setResettingPasswordFor(null);
                      setNewPassword('');
                      setResetPasswordStrength({ strength: 'weak', errors: [] });
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleResetPassword(resettingPasswordFor)}
                    className="btn-primary"
                    disabled={!resetPasswordStrength.isValid || !newPassword.trim()}
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Food Management Section */}
        <div className="card">
          <div className="section-header">
            <h2>Food Management</h2>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportNutritionCSV}
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => fileInputRef.current?.click()}
              >
                Import Nutrition CSV
              </button>
              <button onClick={handleDownloadNutrition} className="btn-secondary">
                Download Nutrition CSV
              </button>
            </div>
          </div>
          <form onSubmit={handleAddFood} className="add-item-form">
            <input
              type="text"
              placeholder="Add new food (Serbian name)"
              value={newFood}
              onChange={(e) => setNewFood(e.target.value)}
              required
              className="large-input"
            />
            <input
              type="text"
              placeholder="English name (optional)"
              value={newFoodEn}
              onChange={(e) => setNewFoodEn(e.target.value)}
              className="large-input"
            />
            <button type="submit" className="btn-primary">Add Food</button>
          </form>
          <div className="items-list">
            <div className="filter-header">
              <h3>Available Foods ({foods.length})</h3>
              <div className="letter-filter">
                <button 
                  className={`letter-btn ${selectedFoodLetter === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedFoodLetter('all')}
                >
                  All
                </button>
                {foodLetters.map(letter => (
                  <button 
                    key={letter}
                    className={`letter-btn ${selectedFoodLetter === letter ? 'active' : ''}`}
                    onClick={() => setSelectedFoodLetter(letter)}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            </div>
            <div className="items-grid">
              {filteredFoods.map((food) => (
                <div key={food.id} className="item-tag" style={{ position: 'relative' }}>
                  <div>{foodDisplayName(food.name, food.name_en)}</div>
                  {(food.protein_per_100g || food.carbs_per_100g || food.fats_per_100g || food.calories_per_100g) ? (
                    <div style={{ fontSize: '10px', marginTop: '4px', color: '#666' }}>
                      P:{food.protein_per_100g || 0}g C:{food.carbs_per_100g || 0}g F:{food.fats_per_100g || 0}g {food.calories_per_100g || 0}kcal
                    </div>
                  ) : (
                    <div style={{ fontSize: '10px', marginTop: '4px', color: '#999', fontStyle: 'italic' }}>
                      No nutrition data
                    </div>
                  )}
                  <button
                    onClick={() => handleEditNutrition(food)}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: '#914c62',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      padding: '2px 6px',
                      fontSize: '10px',
                      cursor: 'pointer'
                    }}
                    title="Edit nutrition"
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Nutrition Edit Modal */}
          {editingNutritionFor && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '30px',
                borderRadius: '10px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}>
                <h3 style={{ marginTop: 0, color: '#1c4e4f' }}>
                  Edit Nutrition for{' '}
                  {(() => {
                    const ef = foods.find((f) => f.id === editingNutritionFor);
                    return foodDisplayName(ef?.name || '', ef?.name_en);
                  })()}
                </h3>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '20px' }}>
                  Values are per 100g of food. For namirnice koje se mere na komad (npr. jaja), možeš da promeniš tip porcije u „po komadu”.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                      English name (optional, shown when users switch to EN):
                    </label>
                    <input
                      type="text"
                      value={nutritionForm.name_en || ''}
                      onChange={(e) => setNutritionForm({ ...nutritionForm, name_en: e.target.value })}
                      placeholder="e.g. Apple"
                      style={{ width: '100%', padding: '8px', border: '2px solid #ddd', borderRadius: '5px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                      Portion type:
                    </label>
                    <select
                      value={nutritionForm.serving_type || 'grams'}
                      onChange={(e) =>
                        setNutritionForm({
                          ...nutritionForm,
                          serving_type: e.target.value,
                          grams_per_serving:
                            e.target.value === 'piece'
                              ? nutritionForm.grams_per_serving || 50
                              : 100
                        })
                      }
                      style={{ width: '100%', padding: '8px', border: '2px solid #ddd', borderRadius: '5px' }}
                    >
                      <option value="grams">Standard – unos u gramima</option>
                      <option value="piece">Po komadu (npr. jaja)</option>
                    </select>
                  </div>
                  {nutritionForm.serving_type === 'piece' && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                        Gramaža jednog komada (g):
                      </label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={nutritionForm.grams_per_serving || 50}
                        onChange={(e) =>
                          setNutritionForm({
                            ...nutritionForm,
                            grams_per_serving: parseFloat(e.target.value) || 50
                          })
                        }
                        style={{ width: '100%', padding: '8px', border: '2px solid #ddd', borderRadius: '5px' }}
                      />
                    </div>
                  )}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                      Protein (g per 100g):
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={nutritionForm.protein_per_100g}
                      onChange={(e) => setNutritionForm({ ...nutritionForm, protein_per_100g: parseFloat(e.target.value) || 0 })}
                      style={{ width: '100%', padding: '8px', border: '2px solid #ddd', borderRadius: '5px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                      Carbs (g per 100g):
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={nutritionForm.carbs_per_100g}
                      onChange={(e) => setNutritionForm({ ...nutritionForm, carbs_per_100g: parseFloat(e.target.value) || 0 })}
                      style={{ width: '100%', padding: '8px', border: '2px solid #ddd', borderRadius: '5px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                      Fats (g per 100g):
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={nutritionForm.fats_per_100g}
                      onChange={(e) => setNutritionForm({ ...nutritionForm, fats_per_100g: parseFloat(e.target.value) || 0 })}
                      style={{ width: '100%', padding: '8px', border: '2px solid #ddd', borderRadius: '5px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                      Calories (kcal per 100g):
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={nutritionForm.calories_per_100g}
                      onChange={(e) => setNutritionForm({ ...nutritionForm, calories_per_100g: parseFloat(e.target.value) || 0 })}
                      style={{ width: '100%', padding: '8px', border: '2px solid #ddd', borderRadius: '5px' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    onClick={() => {
                      setEditingNutritionFor(null);
                      setNutritionForm({
                        protein_per_100g: 0,
                        carbs_per_100g: 0,
                        fats_per_100g: 0,
                        calories_per_100g: 0,
                        serving_type: 'grams',
                        grams_per_serving: 100,
                        name_en: ''
                      });
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveNutrition(editingNutritionFor)}
                    className="btn-primary"
                  >
                    Save Nutrition
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Supplement Management Section */}
        <div className="card">
          <h2>Supplement Management</h2>
          <form onSubmit={handleAddSupplement} className="add-item-form">
            <input
              type="text"
              placeholder="Add new supplement"
              value={newSupplement}
              onChange={(e) => setNewSupplement(e.target.value)}
              required
              className="large-input"
            />
            <button type="submit" className="btn-primary">Add Supplement</button>
          </form>
          <div className="items-list">
            <div className="filter-header">
              <h3>Available Supplements ({supplements.length})</h3>
              <div className="letter-filter">
                <button 
                  className={`letter-btn ${selectedSuppLetter === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedSuppLetter('all')}
                >
                  All
                </button>
                {getSupplementLetters().map(letter => (
                  <button 
                    key={letter}
                    className={`letter-btn ${selectedSuppLetter === letter ? 'active' : ''}`}
                    onClick={() => setSelectedSuppLetter(letter)}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            </div>
            <div className="items-grid">
              {filteredSupplements.map((supplement) => (
                <div key={supplement.id} className="item-tag">
                  {supplement.name}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Exercise Management Section */}
        <div className="card">
          <h2>Exercise Management</h2>
          <form onSubmit={handleAddExercise} className="add-item-form">
            <input
              type="text"
              placeholder="Add new exercise"
              value={newExercise}
              onChange={(e) => setNewExercise(e.target.value)}
              required
              className="large-input"
            />
            <button type="submit" className="btn-primary">Add Exercise</button>
          </form>
          <div className="items-list">
            <div className="filter-header">
              <div>
                <h3>Available Exercises ({exercises.length})</h3>
                {(user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'coach') && (
                  <p className="exercise-management-hint">Click an exercise name to edit it, then Save.</p>
                )}
              </div>
              <div className="letter-filter">
                <button 
                  className={`letter-btn ${selectedExerciseLetter === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedExerciseLetter('all')}
                >
                  All
                </button>
                {getExerciseLetters().map(letter => (
                  <button 
                    key={letter}
                    className={`letter-btn ${selectedExerciseLetter === letter ? 'active' : ''}`}
                    onClick={() => setSelectedExerciseLetter(letter)}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            </div>
            <div className="items-grid">
              {filteredExercises.map((exercise) => {
                const exId = Number.parseInt(String(exercise?.id ?? ''), 10);
                const rowKey = Number.isFinite(exId) ? String(exId) : `name:${exercise.name}`;
                return (
                <div
                  key={rowKey}
                  className={`item-tag item-tag--editable${
                    editingExerciseId === exId ? ' item-tag--editing' : ''
                  }`}
                >
                  {Number.isFinite(exId) && editingExerciseId === exId ? (
                    <form
                      className="exercise-inline-edit"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveExerciseRename(exId);
                      }}
                    >
                      <input
                        type="text"
                        value={editingExerciseDraft}
                        onChange={(e) => setEditingExerciseDraft(e.target.value)}
                        className="exercise-inline-edit-input"
                        autoFocus
                        aria-label="Edit exercise name"
                      />
                      <div className="exercise-inline-edit-actions">
                        <button type="submit" className="btn-primary btn-tiny">
                          Save
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-tiny"
                          onClick={() => {
                            setEditingExerciseId(null);
                            setEditingExerciseDraft('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : Number.isFinite(exId) ? (
                    <button
                      type="button"
                      className="exercise-tag-label"
                      onClick={() => {
                        setEditingExerciseId(exId);
                        setEditingExerciseDraft(exercise.name);
                      }}
                      title="Click to rename this exercise"
                    >
                      {exercise.name}
                    </button>
                  ) : (
                    <span className="exercise-tag-label exercise-tag-label--locked" title="Missing exercise id — refresh the page">
                      {exercise.name}
                    </span>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminConsole;